/**
 * The Requests IDE's data: the client's intakes, the template catalog (saved
 * and curated; schemas fetched lazily per template, since the whole catalog's
 * schemas run to megabytes) and, on demand, one intake's detail with its
 * submissions. One place the explorer, the panes and the chat all read from,
 * refetched by target when a pane writes or the agent reports a change.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";
import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate,
  OutgoingIntakeSubmission,
  OutgoingIntakeTemplate
} from "@/api/types/outgoingIntake";

import { useOnRequestsInvalidate } from "../context/RequestsIdeContext";
import type { RequestsInvalidateTarget } from "../types";

export type IntakeDetail = {
  intake: OutgoingIntake;
  submissions: OutgoingIntakeSubmission[];
};

export type RequestsData = {
  clientId: string;
  intakes: OutgoingIntake[];
  templates: OutgoingIntakeTemplate[];
  savedTemplates: OutgoingIntakeSavedTemplate[];
  isLoading: boolean;
  isTemplatesLoading: boolean;
  /** The catalog failed or timed out; the requests themselves are unaffected. */
  templatesError: string | null;
  retryTemplates: () => void;
  error: string | null;
  /** Refetch by target; every target when omitted. */
  refetch: (targets?: RequestsInvalidateTarget[]) => Promise<void>;
  /** One intake as last fetched, or null; call `loadDetail` to fetch. */
  detail: (intakeId: string) => IntakeDetail | null;
  loadDetail: (
    intakeId: string,
    opts?: { force?: boolean }
  ) => Promise<IntakeDetail | null>;
  /** Replace the cached intake after a local write (PATCH / send). */
  applyIntake: (intake: OutgoingIntake) => void;
  /** Fetch the named templates' schemas into `templates` (idempotent). */
  ensureTemplateSchemas: (templateIds: string[]) => Promise<void>;
  loadTemplateSchema: (templateId: string) => Promise<OutgoingIntakeFormNode[]>;
  templateById: (templateId: string) => OutgoingIntakeTemplate | null;
  savedTemplateById: (templateId: string) => OutgoingIntakeSavedTemplate | null;
  /** Reflect a saved-template create, rename or delete without a refetch. */
  applySavedTemplate: (template: OutgoingIntakeSavedTemplate) => void;
  removeSavedTemplate: (templateId: string) => void;
};

const CATALOG_TIMEOUT_MS = 15_000;

const withTimeout = <T,>(
  promise: Promise<T>,
  ms: number,
  message: string
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => {
        window.clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        window.clearTimeout(timer);
        reject(e);
      }
    );
  });

const RequestsDataContext = createContext<RequestsData | null>(null);

type Props = { clientId: string; children: ReactNode };

export const RequestsDataProvider: FC<Props> = ({ clientId, children }) => {
  const [intakes, setIntakes] = useState<OutgoingIntake[]>([]);
  const [templates, setTemplates] = useState<OutgoingIntakeTemplate[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<
    OutgoingIntakeSavedTemplate[]
  >([]);
  const [details, setDetails] = useState<Record<string, IntakeDetail>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schemaCache = useRef(new Map<string, OutgoingIntakeFormNode[]>());
  const pendingSchemas = useRef(new Map<string, Promise<void>>());
  const pendingDetails = useRef(
    new Map<string, Promise<IntakeDetail | null>>()
  );

  const mergeTemplates = useCallback((incoming: OutgoingIntakeTemplate[]) => {
    const cache = schemaCache.current;
    incoming.forEach((t) => {
      if (t.schema) cache.set(t.template_id, t.schema);
    });
    setTemplates(
      incoming.map((next) => {
        const schema = next.schema ?? cache.get(next.template_id);
        return schema ? { ...next, schema } : next;
      })
    );
  }, []);

  const fetchIntakes = useCallback(async () => {
    const list = await OutgoingIntakesAPI.listForForeignNational(clientId);
    setIntakes(list);
    // Details already loaded follow the list's newer copy of the intake.
    setDetails((prev) => {
      const next = { ...prev };
      for (const intake of list) {
        const cached = next[intake.outgoing_intake_id];
        if (cached) next[intake.outgoing_intake_id] = { ...cached, intake };
      }
      return next;
    });
  }, [clientId]);

  // Saved templates land as soon as they arrive; the catalog can be slow and
  // fails on its own (with a retry) instead of failing the requests.
  const fetchTemplates = useCallback(async () => {
    setIsTemplatesLoading(true);
    setTemplatesError(null);
    const saved = OutgoingIntakesAPI.listSavedTemplates()
      .then(setSavedTemplates)
      .catch(() => undefined);
    try {
      mergeTemplates(
        await withTimeout(
          OutgoingIntakesAPI.listTemplates(),
          CATALOG_TIMEOUT_MS,
          "The template catalog is taking too long to load."
        )
      );
    } catch (e) {
      setTemplatesError(
        e instanceof Error ? e.message : "Couldn't load the template catalog."
      );
    } finally {
      await saved;
      setIsTemplatesLoading(false);
    }
  }, [mergeTemplates]);

  const retryTemplates = useCallback(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  const refetch = useCallback(
    async (targets?: RequestsInvalidateTarget[]) => {
      const wanted = new Set(
        targets ?? ["intakes", "templates", "submissions"]
      );
      const jobs: Promise<unknown>[] = [];
      if (wanted.has("intakes") || wanted.has("submissions"))
        jobs.push(fetchIntakes());
      if (wanted.has("templates")) jobs.push(fetchTemplates());
      if (wanted.has("submissions")) {
        // Every detail already open is refetched so review decisions move live.
        for (const id of Object.keys(details)) {
          jobs.push(
            OutgoingIntakesAPI.get(clientId, id)
              .then((d) =>
                setDetails((prev) => ({
                  ...prev,
                  [id]: {
                    intake: d.outgoing_intake,
                    submissions: d.submissions
                  }
                }))
              )
              .catch(() => undefined)
          );
        }
      }
      try {
        await Promise.all(jobs);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't load requests");
      }
    },
    [clientId, details, fetchIntakes, fetchTemplates]
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    void fetchTemplates();
    fetchIntakes()
      .then(() => {
        if (!cancelled) setError(null);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Couldn't load requests");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchIntakes, fetchTemplates]);

  useOnRequestsInvalidate(["intakes", "templates", "submissions"], (hit) => {
    void refetch(hit);
  });

  // The client submitted while this workspace is open: show it without a reload.
  useRealtimeEvent<{ type?: string; context?: { client_id?: unknown } }>(
    "notification.created",
    (n) => {
      if (n?.type === "intake.submitted" && n.context?.client_id === clientId)
        void refetch(["submissions"]);
    }
  );

  const detail = useCallback(
    (intakeId: string) => details[intakeId] ?? null,
    [details]
  );

  const loadDetail = useCallback(
    async (intakeId: string, opts?: { force?: boolean }) => {
      if (!opts?.force && details[intakeId]) return details[intakeId];
      const pending = pendingDetails.current.get(intakeId);
      if (pending) return pending;
      const job = OutgoingIntakesAPI.get(clientId, intakeId)
        .then((d) => {
          if (!d.outgoing_intake) return null;
          const value: IntakeDetail = {
            intake: d.outgoing_intake,
            submissions: d.submissions
          };
          setDetails((prev) => ({ ...prev, [intakeId]: value }));
          return value;
        })
        .finally(() => pendingDetails.current.delete(intakeId));
      pendingDetails.current.set(intakeId, job);
      return job;
    },
    [clientId, details]
  );

  const applyIntake = useCallback((intake: OutgoingIntake) => {
    setIntakes((prev) => {
      const idx = prev.findIndex(
        (i) => i.outgoing_intake_id === intake.outgoing_intake_id
      );
      if (idx < 0) return [intake, ...prev];
      const next = prev.slice();
      next[idx] = intake;
      return next;
    });
    setDetails((prev) => {
      const cached = prev[intake.outgoing_intake_id];
      return {
        ...prev,
        [intake.outgoing_intake_id]: {
          intake,
          submissions: cached?.submissions ?? []
        }
      };
    });
  }, []);

  const ensureTemplateSchemas = useCallback(async (templateIds: string[]) => {
    const wanted = Array.from(
      new Set(templateIds.map((id) => String(id ?? "").trim()))
    ).filter((id) => id && !schemaCache.current.has(id));
    if (!wanted.length) return;
    const inFlight: Promise<void>[] = [];
    const toFetch: string[] = [];
    for (const id of wanted) {
      const pending = pendingSchemas.current.get(id);
      if (pending) inFlight.push(pending);
      else toFetch.push(id);
    }
    if (toFetch.length) {
      const job = OutgoingIntakesAPI.listTemplates({ templateIds: toFetch })
        .then((incoming) => {
          const cache = schemaCache.current;
          incoming.forEach((t) => {
            if (t.schema) cache.set(t.template_id, t.schema);
          });
          setTemplates((prev) =>
            prev.map((t) => {
              const schema = t.schema ?? cache.get(t.template_id);
              return schema ? { ...t, schema } : t;
            })
          );
        })
        .finally(() =>
          toFetch.forEach((id) => pendingSchemas.current.delete(id))
        );
      toFetch.forEach((id) => pendingSchemas.current.set(id, job));
      inFlight.push(job);
    }
    await Promise.all(inFlight);
  }, []);

  const loadTemplateSchema = useCallback(
    async (templateId: string) => {
      const saved = savedTemplates.find((t) => t.template_id === templateId);
      if (saved) return saved.schema ?? [];
      await ensureTemplateSchemas([templateId]);
      return schemaCache.current.get(templateId) ?? [];
    },
    [ensureTemplateSchemas, savedTemplates]
  );

  const templateById = useCallback(
    (templateId: string) =>
      templates.find((t) => t.template_id === templateId) ?? null,
    [templates]
  );
  const savedTemplateById = useCallback(
    (templateId: string) =>
      savedTemplates.find((t) => t.template_id === templateId) ?? null,
    [savedTemplates]
  );
  const applySavedTemplate = useCallback(
    (template: OutgoingIntakeSavedTemplate) =>
      setSavedTemplates((prev) => {
        const idx = prev.findIndex(
          (t) => t.template_id === template.template_id
        );
        if (idx < 0) return [template, ...prev];
        const next = prev.slice();
        next[idx] = template;
        return next;
      }),
    []
  );
  const removeSavedTemplate = useCallback(
    (templateId: string) =>
      setSavedTemplates((prev) =>
        prev.filter((t) => t.template_id !== templateId)
      ),
    []
  );

  const value = useMemo<RequestsData>(
    () => ({
      clientId,
      intakes,
      templates,
      savedTemplates,
      isLoading,
      isTemplatesLoading,
      templatesError,
      retryTemplates,
      error,
      refetch,
      detail,
      loadDetail,
      applyIntake,
      ensureTemplateSchemas,
      loadTemplateSchema,
      templateById,
      savedTemplateById,
      applySavedTemplate,
      removeSavedTemplate
    }),
    [
      clientId,
      intakes,
      templates,
      savedTemplates,
      isLoading,
      isTemplatesLoading,
      templatesError,
      retryTemplates,
      error,
      refetch,
      detail,
      loadDetail,
      applyIntake,
      ensureTemplateSchemas,
      loadTemplateSchema,
      templateById,
      savedTemplateById,
      applySavedTemplate,
      removeSavedTemplate
    ]
  );

  return (
    <RequestsDataContext.Provider value={value}>
      {children}
    </RequestsDataContext.Provider>
  );
};

export function useRequestsData(): RequestsData {
  const ctx = useContext(RequestsDataContext);
  if (!ctx)
    throw new Error("useRequestsData must be used within RequestsDataProvider");
  return ctx;
}
